// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — the shot a journalist frames and orders as a render
 * (docs/AYNI_XR_STUDIO_TECH_DESIGN.md §3, plan item J3).
 *
 * A shot is a Khipu whose root carries an `x-ayni-shot` extension (the
 * Khipu schema allows `^x-` fields at the root; a step allows no extra
 * properties): the output (aspect, resolution, frame rate, length), the
 * camera and date keyframes, borders (off by default in media renders,
 * audit §13 Q13), the brand kit and the captions. `steps[0]` keeps the
 * layers, palette and first view, so any Khipu player opens a shot
 * sensibly.
 *
 * This module only reads, checks and samples the timeline; the Studio
 * panel builds it and the render mode plays it frame by frame.
 *
 * **Version 2** (Studio motion plan, 10 October 2026): the timeline is the
 * Khipu's own steps, played as stops by `film.ts` (each step's hold,
 * transition and effects, Khipu 1.1); the extension keeps the output, the
 * brand and the finishing (motion blur, end card, animated brand, reframing
 * for other shapes, a voice-over). Version 1 shots (keyframes on one step)
 * still read and render as before.
 */

import { CREDIT_COST } from '../xrPlans'
import { checkBrand, type BrandContent } from './brandKit'
import type { KhipuStep } from '../khipu'
import { planFilm, sampleFilm, type FilmPlan, type FilmSample } from './film'

export const SHOT_EXTENSION = 'x-ayni-shot'

export type ShotAspect = '16:9' | '9:16' | '1:1' | '4:5'
export type ShotResolution = '1080p' | '4k' | '8k'
export type ShotEase = 'linear' | 'inOut'
export type ShotDateMode = 'follow' | 'hold'

export const SHOT_ASPECTS: readonly ShotAspect[] = ['16:9', '9:16', '1:1', '4:5']
export const SHOT_RESOLUTIONS: readonly ShotResolution[] = ['1080p', '4k', '8k']
export const SHOT_FRAME_RATES = [25, 30, 50, 60] as const
/** The longest shot one order renders. */
export const SHOT_MAX_SECONDS = 180
/** The globe's zoom limit (MAX_ZOOM must stay below 10). */
const ZOOM_MAX = 9.99

export interface ShotView {
  lat: number
  lon: number
  zoom: number
  bearing: number
  pitch: number
}

export interface ShotKeyframe {
  /** Seconds from the start of the shot. */
  t: number
  view: ShotView
  /** ISO date-time of the data at this keyframe. */
  at: string
  /** How the move into the next keyframe eases. */
  ease: ShotEase
}

export interface ShotCaption {
  t0: number
  t1: number
  lat: number
  lon: number
  label: string
}

/**
 * The file a render delivers (plan J7): `mp4` H.264 (H.265 at 8K), `hevc`
 * H.265, `prores` ProRes 4444 in a .mov (with alpha when `transparent`, for
 * keying over a studio background), `gif` for the web (1080p shots only,
 * drawn at {@link GIF_FPS} and at most {@link GIF_MAX_SECONDS}), `png` one
 * still at `stillAt` seconds.
 */
export type ShotFormat = 'mp4' | 'hevc' | 'prores' | 'gif' | 'png'
export const SHOT_FORMATS: readonly ShotFormat[] = ['mp4', 'hevc', 'prores', 'gif', 'png']
export const GIF_FPS = 15
export const GIF_MAX_SECONDS = 20
/** Formats that can leave space transparent instead of drawing the stars. */
export const ALPHA_FORMATS: readonly ShotFormat[] = ['prores', 'png']
/** Each format's file name extension and media type. */
export const FORMAT_FILE: Record<ShotFormat, { ext: string; type: string }> = {
  mp4: { ext: 'mp4', type: 'video/mp4' },
  hevc: { ext: 'mp4', type: 'video/mp4' },
  prores: { ext: 'mov', type: 'video/quicktime' },
  gif: { ext: 'gif', type: 'image/gif' },
  png: { ext: 'png', type: 'image/png' },
}

export interface ShotOutput {
  aspect: ShotAspect
  resolution: ShotResolution
  fps: (typeof SHOT_FRAME_RATES)[number]
  seconds: number
  format: ShotFormat
  /** Space left transparent (no stars or atmosphere); only for {@link ALPHA_FORMATS}. */
  transparent: boolean
  /** The moment a `png` still shows, in seconds from the start. */
  stillAt: number
}

/** What only a Newsroom licence may order (plan J7): 8K and ProRes. */
export const needsNewsroom = (output: ShotOutput): boolean => output.resolution === '8k' || output.format === 'prores'

/** Finishing touches of a version 2 shot. */
export interface ShotFinish {
  /** Frames blended from several moments, so fast moves look filmed (2× credits). */
  motionBlur: boolean
  /** A closing card with every data credit, the date and the AYNI mark. */
  endCard: boolean
  /** The logo and lower third slide in and out. */
  animatedBrand: boolean
  /** In a shape narrower than 16:9 the camera pulls back so each stop stays in frame. */
  reframe: boolean
  /** The outlet's own voice-over, uploaded to the Studio, mixed under the video. */
  voiceOver: { id: string; name: string } | null
}

export const NO_FINISH: ShotFinish = { motionBlur: false, endCard: false, animatedBrand: false, reframe: false, voiceOver: null }
/** How long the end card stays. */
export const END_CARD_SECONDS = 4
/** Moments blended into one frame with motion blur. */
export const MOTION_BLUR_SAMPLES = 6

export interface Shot {
  version: 1 | 2
  output: ShotOutput
  /** Version 1's camera and date keyframes; empty in version 2. */
  keyframes: ShotKeyframe[]
  dateMode: ShotDateMode
  borders: boolean
  brandKit: string | null
  /** A copy of the brand kit the shot uses (logo, lower third, accent), checked; null for none. */
  brand: BrandContent | null
  /** Version 1's captions (version 2 uses each stop's callouts). */
  captions: ShotCaption[]
  /** Version 2: the stops on their timeline; null in version 1. */
  film: FilmPlan | null
  finish: ShotFinish
}

/** Pixel size of each aspect at each resolution (the long side follows the resolution's 16:9 width or height). */
const SHORT_SIDE: Record<ShotResolution, number> = { '1080p': 1080, '4k': 2160, '8k': 4320 }

export function outputSize(aspect: ShotAspect, resolution: ShotResolution): { width: number; height: number } {
  const s = SHORT_SIDE[resolution]
  switch (aspect) {
    case '16:9': return { width: (s * 16) / 9, height: s }
    case '9:16': return { width: s, height: (s * 16) / 9 }
    case '1:1': return { width: s, height: s }
    case '4:5': return { width: (s * 4) / 5, height: s }
  }
}

/**
 * Broadcast safe areas as fractions of each side: titles and the credit stay
 * inside title-safe (90 %), anything that must be seen inside action-safe (93 %).
 */
export const SAFE_AREA = { action: 0.035, title: 0.05 } as const

/** Frames a second as drawn: a GIF is drawn at {@link GIF_FPS} whatever the shot's rate. */
export const renderFps = (output: ShotOutput): number => (output.format === 'gif' ? GIF_FPS : output.fps)

/** Frames to draw: one for a still. */
export const frameCount = (output: ShotOutput): number =>
  output.format === 'png' ? 1 : Math.round(output.seconds * renderFps(output))

/** The shot time of frame `i`, in seconds. */
export const frameTime = (output: ShotOutput, i: number): number =>
  output.format === 'png' ? output.stillAt : i / renderFps(output)

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const record = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : null

function readView(value: unknown): ShotView | null {
  const v = record(value)
  if (!v || !isNum(v.lat) || !isNum(v.lon) || !isNum(v.zoom)) return null
  if (Math.abs(v.lat) > 90 || v.zoom < 0 || v.zoom > ZOOM_MAX) return null
  const pitch = isNum(v.pitch) ? v.pitch : 0
  if (pitch < 0 || pitch > 85) return null
  return { lat: v.lat, lon: v.lon, zoom: v.zoom, bearing: isNum(v.bearing) ? v.bearing : 0, pitch }
}

/**
 * Read a Khipu's `x-ayni-shot`, or null when it is missing or unusable:
 * fewer than one keyframe, keyframes out of order or past the end, a date
 * that does not parse, an unknown preset, or a length over
 * {@link SHOT_MAX_SECONDS}. Borders default to off.
 */
export function parseShot(khipu: unknown, resolveRelative?: (step: KhipuStep, edge: 'first' | 'latest') => number | null): Shot | null {
  const ext = record(record(khipu)?.[SHOT_EXTENSION])
  if (!ext || (ext.version !== 1 && ext.version !== 2)) return null
  const out = record(ext.output)
  if (!out || !SHOT_ASPECTS.includes(out.aspect as ShotAspect) || !SHOT_RESOLUTIONS.includes(out.resolution as ShotResolution)) return null
  if (!SHOT_FRAME_RATES.includes(out.fps as ShotOutput['fps'])) return null
  if (ext.version === 2) return parseFilmShot(khipu, ext, out, resolveRelative)
  if (!isNum(out.seconds) || out.seconds <= 0 || out.seconds > SHOT_MAX_SECONDS) return null
  // Shots saved before formats existed are MP4.
  const format = out.format === undefined ? 'mp4' : out.format as ShotFormat
  if (!SHOT_FORMATS.includes(format)) return null
  if (format === 'gif' && (out.resolution !== '1080p' || out.seconds > GIF_MAX_SECONDS)) return null
  const stillAt = isNum(out.stillAt) ? out.stillAt : 0
  if (stillAt < 0 || stillAt > out.seconds) return null
  const output: ShotOutput = {
    aspect: out.aspect as ShotAspect, resolution: out.resolution as ShotResolution, fps: out.fps as ShotOutput['fps'], seconds: out.seconds,
    format, transparent: out.transparent === true && ALPHA_FORMATS.includes(format), stillAt,
  }

  const keyframes: ShotKeyframe[] = []
  for (const entry of Array.isArray(ext.keyframes) ? ext.keyframes : []) {
    const k = record(entry)
    const view = readView(k?.view)
    if (!k || !view || !isNum(k.t) || typeof k.at !== 'string' || Number.isNaN(Date.parse(k.at))) return null
    if (k.t < 0 || k.t > output.seconds) return null
    if (keyframes.length && k.t <= keyframes[keyframes.length - 1].t) return null
    keyframes.push({ t: k.t, view, at: k.at, ease: k.ease === 'linear' ? 'linear' : 'inOut' })
  }
  if (!keyframes.length) return null

  const captions: ShotCaption[] = (Array.isArray(ext.captions) ? ext.captions : []).flatMap((entry): ShotCaption[] => {
    const c = record(entry)
    return c && isNum(c.t0) && isNum(c.t1) && c.t1 > c.t0 && isNum(c.lat) && isNum(c.lon) && typeof c.label === 'string'
      ? [{ t0: c.t0, t1: c.t1, lat: c.lat, lon: c.lon, label: c.label.slice(0, 80) }]
      : []
  })
  return {
    version: 1,
    output,
    keyframes,
    dateMode: ext.dateMode === 'hold' ? 'hold' : 'follow',
    borders: ext.borders === true,
    brandKit: typeof ext.brandKit === 'string' && ext.brandKit ? ext.brandKit : null,
    brand: checkBrand(ext.brand),
    captions,
    film: null,
    finish: NO_FINISH,
  }
}

function readFinish(value: unknown): ShotFinish {
  const f = record(value) ?? {}
  const voice = record(f.voiceOver)
  return {
    motionBlur: f.motionBlur === true,
    endCard: f.endCard === true,
    animatedBrand: f.animatedBrand !== false,
    reframe: f.reframe !== false,
    voiceOver: voice && typeof voice.id === 'string' && /^[a-f0-9]{16,32}$/.test(voice.id)
      ? { id: voice.id, name: typeof voice.name === 'string' ? voice.name.slice(0, 120) : '' }
      : null,
  }
}

/** A version 2 shot: the stops come from the Khipu's steps; its length from them. */
function parseFilmShot(
  khipu: unknown, ext: Record<string, unknown>, out: Record<string, unknown>,
  resolveRelative?: (step: KhipuStep, edge: 'first' | 'latest') => number | null,
): Shot | null {
  const steps = record(record(khipu)?.path)?.steps
  if (!Array.isArray(steps)) return null
  const film = planFilm(steps as KhipuStep[], resolveRelative)
  if (!film) return null
  const finish = readFinish(ext.finish)
  const seconds = film.seconds + (finish.endCard ? END_CARD_SECONDS : 0)
  if (seconds <= 0 || seconds > SHOT_MAX_SECONDS) return null
  const format = out.format === undefined ? 'mp4' : out.format as ShotFormat
  if (!SHOT_FORMATS.includes(format)) return null
  if (format === 'gif' && (out.resolution !== '1080p' || seconds > GIF_MAX_SECONDS)) return null
  const stillAt = isNum(out.stillAt) ? out.stillAt : 0
  if (stillAt < 0 || stillAt > seconds) return null
  const output: ShotOutput = {
    aspect: out.aspect as ShotAspect, resolution: out.resolution as ShotResolution, fps: out.fps as ShotOutput['fps'], seconds,
    format, transparent: out.transparent === true && ALPHA_FORMATS.includes(format), stillAt,
  }
  return {
    version: 2, output, keyframes: [], dateMode: 'follow', borders: ext.borders === true,
    brandKit: typeof ext.brandKit === 'string' && ext.brandKit ? ext.brandKit : null,
    brand: checkBrand(ext.brand), captions: [], film, finish,
  }
}

/**
 * How far the camera pulls back in a shape narrower than 16:9 so a stop framed
 * for 16:9 stays in the picture (most, not all, of its width: a vertical clip
 * would otherwise shrink the subject too far).
 */
export function reframeZoom(aspect: ShotAspect): number {
  const ratio = { '16:9': 16 / 9, '9:16': 9 / 16, '1:1': 1, '4:5': 4 / 5 }[aspect] / (16 / 9)
  return ratio >= 1 ? 0 : 0.6 * Math.log2(ratio)
}

/** Smooth in and out (cubic), the default between keyframes. */
const easeInOut = (x: number): number => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2)
/** The shortest way round between two angles in degrees, as a signed delta. */
const angleDelta = (from: number, to: number): number => ((((to - from) % 360) + 540) % 360) - 180
const wrapLon = (lon: number): number => ((((lon + 180) % 360) + 360) % 360) - 180

export interface ShotSample {
  view: ShotView
  /** Epoch ms of the data to show; null leaves the dataset's date as it is (version 2). */
  atMs: number | null
  /** Version 2: the stop, its phase and the data move at this moment. */
  film?: FilmSample
  /** Version 2: 0–1 through the end card, or null before it. */
  endCard?: number | null
}

/** Camera and data date at `t` seconds: held before the first keyframe and after the last. */
export function sampleShot(shot: Shot, t: number): ShotSample {
  if (shot.film) {
    const plan = shot.film
    const film = sampleFilm(plan, Math.min(t, plan.seconds))
    const pull = shot.finish.reframe ? reframeZoom(shot.output.aspect) : 0
    const view = { ...film.view, zoom: Math.max(0, film.view.zoom + pull) }
    const endCard = shot.finish.endCard && t > plan.seconds ? Math.min(1, (t - plan.seconds) / END_CARD_SECONDS) : null
    return { view, atMs: film.data.atMs, film, endCard }
  }
  const ks = shot.keyframes
  const first = ks[0]
  const holdMs = Date.parse(first.at)
  let a = first, b = first
  if (t <= first.t) { a = b = first } else if (t >= ks[ks.length - 1].t) { a = b = ks[ks.length - 1] } else {
    for (let i = 0; i < ks.length - 1; i++) if (t >= ks[i].t && t <= ks[i + 1].t) { a = ks[i]; b = ks[i + 1]; break }
  }
  const raw = a === b ? 0 : (t - a.t) / (b.t - a.t)
  const x = a.ease === 'linear' ? raw : easeInOut(raw)
  const lerp = (p: number, q: number) => p + (q - p) * x
  const view: ShotView = {
    lat: lerp(a.view.lat, b.view.lat),
    lon: wrapLon(a.view.lon + angleDelta(a.view.lon, b.view.lon) * x),
    zoom: lerp(a.view.zoom, b.view.zoom),
    bearing: ((a.view.bearing + angleDelta(a.view.bearing, b.view.bearing) * x) % 360 + 360) % 360,
    pitch: lerp(a.view.pitch, b.view.pitch),
  }
  // Dates follow the keyframes linearly (time should not ease), or hold the first.
  const atMs = shot.dateMode === 'hold'
    ? holdMs
    : Date.parse(a.at) + (Date.parse(b.at) - Date.parse(a.at)) * raw
  return { view, atMs }
}

/** Camera and date of frame `i`. */
export const sampleFrame = (shot: Shot, i: number): ShotSample => sampleShot(shot, frameTime(shot.output, i))

/** Captions on screen at `t` seconds. */
export const captionsAt = (shot: Shot, t: number): ShotCaption[] => shot.captions.filter((c) => t >= c.t0 && t < c.t1)

const PER_MINUTE: Record<ShotResolution, number> = {
  '1080p': CREDIT_COST.hd1080PerMinute,
  '4k': CREDIT_COST.uhd4kPerMinute,
  '8k': CREDIT_COST.uhd8kPerMinute,
}

/**
 * The render's price in credits: a still costs {@link CREDIT_COST.still} per
 * globe it shows; anything moving is per minute at its resolution (a GIF at
 * 1080p's) and per globe on screen (`globeSeconds`), rounded up, at least 1.
 * ProRes and H.265 cost the same as MP4.
 */
export function shotQuote(
  output: ShotOutput,
  finish: Pick<ShotFinish, 'motionBlur'> = NO_FINISH,
  globeSecs: number = output.seconds,
  stillGlobes: number = 1,
): number {
  if (output.format === 'png') return CREDIT_COST.still * stillGlobes
  // Rounded to a thousandth of a credit before rounding up, so float noise never adds a credit.
  const exact = Math.round((globeSecs * PER_MINUTE[output.resolution] * 1000) / 60) / 1000
  const base = Math.max(1, Math.ceil(exact))
  // Motion blur draws several moments per frame: twice the credits (owner, 10 October 2026).
  return finish.motionBlur ? base * 2 : base
}

/**
 * Seconds of video times the globes on screen (owner, 10 October 2026: a
 * split is priced per globe). Each stop counts from its arrival to its end
 * with its globes; what follows the stops (the end card) and a version 1
 * shot count one globe. A video on one globe is its length.
 */
export function globeSeconds(film: FilmPlan | null, seconds: number): number {
  if (!film) return seconds
  let total = 0
  for (const stop of film.stops) total += (stop.end - stop.start) * stop.globes
  return total + Math.max(0, seconds - film.seconds)
}

/** The globes on screen at `t` seconds (one outside the stops). */
export function globesAt(film: FilmPlan | null, t: number): number {
  return film?.stops.find((stop) => t >= stop.start && t < stop.end)?.globes ?? 1
}

/** A shot's price: its output, its finishing and the globes it shows. */
export const quoteShot = (shot: Shot): number =>
  shotQuote(shot.output, shot.finish, globeSeconds(shot.film, shot.output.seconds), globesAt(shot.film, shot.output.stillAt))
